// The only place destructive actions live.
//
//   suspend    POST …/suspend { reason ≥ 5 chars }: locks every public route,
//              Fan AI and the league admin
//   reinstate  POST …/reinstate: restores the status it had before
//   delete     DELETE … { confirm_subdomain }: the subdomain typed exactly
//
// All three go through ConfirmDialog, which stays open while the request
// runs and shows the server's error (reason_required, confirmation_mismatch)
// inside the dialog.

import { useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '@/api'
import { Button, Card, CardHeader, ConfirmDialog, KeyValueList, useToast } from '@/components/ui'
import { formatDateTime } from '@/lib/format'
import { invalidateQueries } from '@/lib/query'
import { accountStatusLabel } from '@/lib/status'
import { useTenant } from '../tenantContext'

function DangerRow({ title, body, action }: { title: string; body: ReactNode; action: ReactNode }) {
  return (
    <div className="flex flex-col gap-3 border-t border-line py-4 first:border-t-0 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="font-semibold text-ink">{title}</p>
        <div className="mt-0.5 text-[13px] text-muted">{body}</div>
      </div>
      <div className="shrink-0">{action}</div>
    </div>
  )
}

export default function DangerZoneTab() {
  const { tenant, apply } = useTenant()
  const toast = useToast()
  const navigate = useNavigate()
  const [dialog, setDialog] = useState<'suspend' | 'reinstate' | 'delete' | null>(null)
  const suspended = tenant.account.status === 'suspended'
  const suspension = tenant.account.suspension

  return (
    <div className="grid grid-cols-1 gap-4">
      {suspended ? (
        <Card>
          <CardHeader title="Current suspension" />
          <KeyValueList
            items={[
              { label: 'Reason', value: suspension?.reason ?? <span className="text-muted">Not recorded</span> },
              {
                label: 'By',
                value: suspension?.suspended_by ? (
                  <span className="break-all">{suspension.suspended_by.name ? `${suspension.suspended_by.name} (${suspension.suspended_by.email})` : suspension.suspended_by.email}</span>
                ) : (
                  <span className="text-muted">Not recorded</span>
                ),
              },
              { label: 'Since', value: formatDateTime(suspension?.suspended_at) },
            ]}
          />
        </Card>
      ) : null}

      <Card tone="danger">
        <CardHeader title="Danger zone" description="These change what fans and league admins can reach. Every action is logged with who did it." />
        <DangerRow
          title={suspended ? 'Reinstate tenant' : 'Suspend tenant'}
          body={
            suspended
              ? 'Brings the public site and the league admin back, with the plan status it had before it was suspended.'
              : 'Takes every public page offline, Fan AI included, and locks the league admin. Nothing is deleted; you can reinstate it.'
          }
          action={
            <Button variant={suspended ? 'secondary' : 'danger'} onClick={() => setDialog(suspended ? 'reinstate' : 'suspend')}>
              {suspended ? 'Reinstate…' : 'Suspend…'}
            </Button>
          }
        />
        <DangerRow
          title="Delete tenant"
          body="Permanently deletes the league and everything in it: seasons, games, stats, pages, forms and memberships. Can't be undone."
          action={
            <Button variant="danger" onClick={() => setDialog('delete')}>
              Delete…
            </Button>
          }
        />
      </Card>

      <ConfirmDialog
        open={dialog === 'suspend'}
        onClose={() => setDialog(null)}
        title={`Suspend ${tenant.name}?`}
        description="The public site and Fan AI go offline, and league admins are locked out, until you reinstate it. The reason is saved in the audit log."
        confirmLabel="Suspend tenant"
        reason={{ label: 'Reason', placeholder: 'e.g. Unpaid since August, agreed with the organiser', minLength: 5 }}
        onConfirm={async ({ reason }) => {
          const next = await api.tenants.suspend(tenant.id, reason!)
          apply(next)
          toast.success(`${tenant.name} is suspended.`)
        }}
      />
      <ConfirmDialog
        open={dialog === 'reinstate'}
        onClose={() => setDialog(null)}
        title={`Reinstate ${tenant.name}?`}
        description="The public site and league admin come back online with the plan status the tenant had before it was suspended."
        confirmLabel="Reinstate"
        tone="primary"
        onConfirm={async () => {
          const next = await api.tenants.reinstate(tenant.id)
          apply(next)
          toast.success(`${tenant.name} is back: ${accountStatusLabel(next.account.status).toLowerCase()}${next.account.is_live ? ' and live' : ', site still offline'}.`)
        }}
      />
      <ConfirmDialog
        open={dialog === 'delete'}
        onClose={() => setDialog(null)}
        title={`Delete ${tenant.name}?`}
        description={
          <>
            This permanently deletes the league and all of its data, then removes <span className="font-mono text-ink">{tenant.domains.subdomain_host || tenant.subdomain}</span>
            {tenant.custom_domain ? (
              <>
                {' '}
                and <span className="font-mono text-ink">{tenant.custom_domain}</span>
              </>
            ) : null}{' '}
            from Cloudflare.
          </>
        }
        confirmLabel="Delete permanently"
        typeToConfirm={tenant.subdomain}
        onConfirm={async () => {
          await api.tenants.delete(tenant.id, tenant.subdomain)
          invalidateQueries('tenants')
          invalidateQueries('dashboard')
          toast.success(`${tenant.name} was deleted.`)
          navigate('/tenants', { replace: true })
        }}
      />
    </div>
  )
}
